import * as React from "react";

/**
 * Viewport width below which the desktop-first studio UI is considered
 * unusable. Matches Tailwind's `md` breakpoint (768px) — anything narrower
 * than a small tablet in portrait triggers the "screen too small" overlay.
 */
export const SMALL_SCREEN_MEDIA_QUERY = "(max-width: 767.98px)";

/**
 * Returns `true` while the viewport is narrower than the desktop breakpoint.
 * Subscribes to `matchMedia` change events so it stays in sync on resize /
 * device rotation. SSR-safe: defaults to `false` when `window.matchMedia`
 * is unavailable.
 */
export function useIsSmallScreen(): boolean {
  const [isSmall, setIsSmall] = React.useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(SMALL_SCREEN_MEDIA_QUERY).matches;
  });

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(SMALL_SCREEN_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) =>
      setIsSmall(event.matches);
    // Resync in case the viewport changed between initial render and effect.
    setIsSmall(mql.matches);
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  return isSmall;
}
